import { useParams, Link } from "react-router-dom";
import { ArrowRight, Phone, Home, Building2, ChevronRight, CheckCircle, XCircle } from "lucide-react";
import { getServiceBySlug, SERVICES, SERVICE_DIY, SERVICE_FAQS } from "@/data/services";
import { BUSINESS } from "@/data/businessInfo";
import { SEO, ServiceSchema, FAQSchema } from "@/components/SEO";
import { LocationCardsSection } from "@/components/SharedSections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Service Not Found</h1>
        <Link to="/services" className="text-[#BF8D30] font-semibold">Back to Services</Link>
      </div>
    );
  }

  const isResidential = service.type === "residential";
  const isCommercialHub = slug === "commercial-electrical-troubleshooting";
  const accentColor = isResidential ? "#BF8D30" : "#317E8C";
  const TypeIcon = isResidential ? Home : Building2;
  const diy = SERVICE_DIY[service.id] || { canDo: [], callPro: [] };
  const faqs = SERVICE_FAQS[service.id] || [];

  return (
    <div>
      <SEO title={isCommercialHub ? "Commercial Electrical Services Port St. Lucie | Southern Light Solutions" : service.data.metaTitle?.split('|')[0]?.trim()} description={service.data.metaDescription} path={`/services/${slug}`} noindex={!isResidential && !isCommercialHub} />
      <ServiceSchema serviceName={service.title} serviceDescription={service.description} serviceType={isResidential ? "Residential Electrical Service" : "Commercial Electrical Service"} />
      {faqs.length > 0 && <FAQSchema faqs={faqs} />}
      {/* Breadcrumb */}
      <div className="bg-[#FAFAFA] border-b border-[#6B3A1F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-xs text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Link to="/" className="hover:text-[#BF8D30]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/services" className="hover:text-[#BF8D30]">Services</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#401303] font-semibold">{service.title}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#FAFAFA] py-12 lg:py-20" data-testid="service-detail-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-2 mb-4">
                <TypeIcon className="w-4 h-4" style={{ color: accentColor }} />
                <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accentColor, fontFamily: 'Outfit, sans-serif' }}>{isResidential ? 'Residential' : 'Commercial'}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#401303] tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.data.metaTitle?.split('|')[0]?.trim() || service.title}</h1>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 font-bold rounded-full text-sm transition-colors duration-200" style={{ backgroundColor: accentColor, color: isResidential ? '#401303' : '#fff', fontFamily: 'Outfit, sans-serif' }} data-testid="service-book-btn">
                  Request an Estimate <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#401303] text-[#401303] font-bold rounded-full text-sm hover:bg-[#401303] hover:text-white transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="service-call-btn">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
              <div className="prose max-w-none">
                {service.data.content.split('\n\n').map((para, i) => (
                  <p key={i} className="text-[#6B3A1F] leading-relaxed mb-4 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>{para.trim()}</p>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="sticky top-40">
              <img src={service.image} alt={`${service.title} by Southern Light Solutions`} className="w-full h-[350px] lg:h-[400px] object-cover object-center rounded-2xl border border-[#6B3A1F]/10 shadow-xl" />
              {/* Related services sidebar */}
              <div className="mt-6 bg-white rounded-2xl border border-[#6B3A1F]/10 p-6">
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {isResidential ? 'Also Available for Commercial' : 'Also Available for Residential'}
                </h3>
                <Link to={`/services/${isResidential ? service.commercial.slug : service.residential.slug}`} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-[#6B3A1F]/10 hover:border-[#BF8D30]/30 transition-colors duration-200 group" data-testid="service-switch-type">
                  {isResidential ? <Building2 className="w-4 h-4 text-[#317E8C]" /> : <Home className="w-4 h-4 text-[#BF8D30]" />}
                  <span className="text-sm font-semibold text-[#401303] group-hover:text-[#BF8D30]" style={{ fontFamily: 'Outfit, sans-serif' }}>{isResidential ? 'Commercial' : 'Residential'} {service.title}</span>
                </Link>
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#401303] mt-6 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Other Services</h3>
                <div className="space-y-1">
                  {SERVICES.filter(s => s.id !== service.id).slice(0, 5).map(s => (
                    <Link key={s.id} to={`/services/${isResidential ? s.residential.slug : s.commercial.slug}`} className="block px-3 py-2 text-sm text-[#6B3A1F] hover:text-[#BF8D30] rounded-lg hover:bg-[#FAFAFA] transition-colors duration-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DIY Section */}
      {(diy.canDo.length > 0 || diy.callPro.length > 0) && (
        <section className="bg-white py-16 lg:py-20" data-testid="diy-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>DIY vs. Pro</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>What You Can Do vs. When to Call a Pro</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Can Do */}
              <div className="bg-[#FAFAFA] rounded-2xl border border-[#6B3A1F]/10 p-6">
                <h3 className="text-base font-bold text-[#317E8C] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <CheckCircle className="w-5 h-5" /> Safe to Do Yourself
                </h3>
                <ul className="space-y-3">
                  {diy.canDo.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#6B3A1F] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Call Pro */}
              <div className="bg-[#FAFAFA] rounded-2xl border border-[#BF3604]/15 p-6">
                <h3 className="text-base font-bold text-[#BF3604] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <XCircle className="w-5 h-5" /> Call a Licensed Electrician
                </h3>
                <ul className="space-y-3">
                  {diy.callPro.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XCircle className="w-4 h-4 text-[#BF3604] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#6B3A1F] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-6 text-center">
              <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm hover:bg-[#D97904] hover:text-white transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="diy-cta">
                Schedule Your {service.title} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Service FAQ */}
      {faqs.length > 0 && (
        <section className="bg-[#FAFAFA] py-16 lg:py-20" data-testid="service-faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Questions</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.title} FAQ</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`svc-faq-${i}`} className="bg-white rounded-xl border border-[#6B3A1F]/10 px-5 overflow-hidden">
                  <AccordionTrigger className="text-left text-sm font-bold text-[#401303] py-4 hover:text-[#BF8D30] hover:no-underline" style={{ fontFamily: 'Outfit, sans-serif' }}>{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-[#6B3A1F] leading-relaxed pb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <LocationCardsSection title="Areas We Serve" />

      {/* Bottom CTA */}
      <section className="bg-warm-gradient py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready for Your {service.title}?</h2>
          <p className="text-white/80 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Get a free estimate from a licensed electrician you can trust.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#401303] font-bold rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="service-bottom-book-btn">
              Request an Estimate <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-white text-white font-bold rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
